import { RefObject, useLayoutEffect, useState } from 'react';

// Below this the pool filters leave no room for the people list, so the page
// scrolls once instead and each panel keeps its own scroll.
export const MIN_PANELS_HEIGHT = 560;

const EDGE_SIZE = 64;
const MAX_EDGE_STEP = 20;

interface PanelsHeightInput {
  viewportHeight: number;
  paddingBottom: number;
  offsetTop: number;
  footerHeight: number;
}

export function panelsHeight({
  viewportHeight,
  paddingBottom,
  offsetTop,
  footerHeight,
}: PanelsHeightInput) {
  const available = viewportHeight - paddingBottom - offsetTop - footerHeight;
  return Math.max(MIN_PANELS_HEIGHT, Math.floor(available));
}

// Pixels to scroll a panel while dragging near its top or bottom edge;
// grows as the pointer gets closer to the edge.
export function edgeScrollDelta(
  clientY: number,
  box: { top: number; bottom: number }
) {
  const fromTop = clientY - box.top;
  if (fromTop < EDGE_SIZE) {
    return -Math.ceil(((EDGE_SIZE - fromTop) / EDGE_SIZE) * MAX_EDGE_STEP);
  }
  const fromBottom = box.bottom - clientY;
  if (fromBottom < EDGE_SIZE) {
    return Math.ceil(((EDGE_SIZE - fromBottom) / EDGE_SIZE) * MAX_EDGE_STEP);
  }
  return 0;
}

function scrollParent(el: HTMLElement): HTMLElement {
  let node = el.parentElement;
  while (node) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
    node = node.parentElement;
  }
  return document.documentElement;
}

// Height that lets the panels fill the visible area above the footer.
export function usePanelsHeight(
  panelsRef: RefObject<HTMLElement>,
  footerRef: RefObject<HTMLElement>
) {
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const panels = panelsRef.current;
    if (!panels) return undefined;
    const container = scrollParent(panels);

    const measure = () => {
      const styles = getComputedStyle(container);
      const containerTop =
        container === document.documentElement
          ? 0
          : container.getBoundingClientRect().top;
      setHeight(
        panelsHeight({
          viewportHeight: container.clientHeight,
          paddingBottom: parseFloat(styles.paddingBottom) || 0,
          offsetTop:
            panels.getBoundingClientRect().top -
            containerTop +
            container.scrollTop,
          footerHeight: footerRef.current?.offsetHeight || 0,
        })
      );
    };

    measure();
    window.addEventListener('resize', measure);
    // The header wraps and the supervisor bar comes and goes.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    if (panels.parentElement) observer?.observe(panels.parentElement);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [panelsRef, footerRef]);

  return height;
}
